
// import Vue from 'vue';
// import Vuex from 'vuex';

import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import { isEmpty, isNil, isArray, partition, concat, isString, isObject, sortBy, filter } from 'lodash';
import axios from '@/lib/util/axios';
import { optimizeInputData } from '@/lib/util/storeUtils';
import ServiceUrls from '@/lib/ServiceUrls';
import uuid from 'uuid';
import { getToken, setToken, clearToken } from '../lib/util/token';

export default {
  namespaced: true,
  state: {
    hotelId: null,
    hotels: [],
    hotelSettings: {},
    loaded: false,
    accessibleHotels: 1,
    username: null,
    user: null,
    tabId: uuid.v4(),
    ws: null,
    wsInitialized: false,
    historyLength: -1,
    // token: null,
  },
  mutations: {
    HISTORY_LENGTH(state, newHistoryLength) {
      state.historyLength = newHistoryLength;
    },
    HOTEL_SETTINGS(state, newHotelSettings) {
      state.hotelSettings = newHotelSettings;
    },
    LOADED(state, newValue) {
      state.loaded = newValue;
    },
    ACCESSIBLE_HOTELS(state, newValue) {
      state.accessibleHotels = newValue;
    },
    HOTELS(state, newValue) {
      state.hotels = newValue;
    },
    HOTEL_ID(state, newValue) {
      state.hotelId = newValue;
    },
    USER(state, newValue) {
      state.user = newValue;
    },
    USERNAME(state, newValue) {
      state.username = newValue;
    },
    WEBSOCKET(state, newValue) {
      if (state.ws !== newValue || isNil(newValue)) state.wsInitialized = false;
      if (!isNil(state.ws) && state.ws.readyState <= 1) {
        try {
          state.ws.close();
        } catch (e) { /* ignore */ }
      }
      state.ws = newValue;
    },
    WEBSOCKET_INITIALIZED(state, newValue) {
      state.wsInitialized = newValue;
    },
    // TOKEN(state, newValue) {
    //   state.token = newValue;
    // },
  },
  getters: {
    hotelId: state => state.hotelId,
    hotelSettings: state => state.hotelSettings,
    loaded: state => state.loaded,
    accessibleHotels: state => state.accessibleHotels,
    hotels: state => state.hotels,
    user: state => state.user,
    username: state => state.username,
    tabId: state => state.tabId,
    webSocket: state => state.ws,
    webSocketInitialized: state => state.wsInitialized,
    historyLength: state => state.historyLength,
    // token: state => state.token,
  },
  actions: {
    updateAccessibleHotels(context, data) {
      axios.get(ServiceUrls.hotel.list(this), {
        headers: {
          'x-hotelsoftware-hotelid': context.getters.hotelId,
        },
      })
        .then((response) => {
          context.commit('ACCESSIBLE_HOTELS', response.data.data.length);
          context.commit('HOTELS', response.data.data);

          if (response.data.data.length > 1) {
            localStorage.setItem(`instances_${process.env.HOTELMEISTER_ENV}`, response.data.data.map(it => it.subdomain).join(','));
          }
        })
        .catch((error) => {
          context.commit('ACCESSIBLE_HOTELS', 0);
          context.commit('HOTELS', []);
        });
    },
    logout(context, data) {
      const token = getToken(context.getters.hotelId);

      const clear = function () {
        clearToken(context.getters.hotelId);
        context.commit('USER', null);
        context.commit('USERNAME', null);
        context.dispatch('unloadHotel');
      };


      if (!isEmpty(token)) {
        axios.post(ServiceUrls.logout())
          .then((userResponse) => {
            clear();
          })
          .catch((error) => {
            clear();
            return Promise.reject(error);
          });
      } else {
        clear();
      }

      const ws = context.getters.ws;
      try {
        if (ws != null) ws.close();
      } catch (e) { /* ignore */ }
      context.commit('WEBSOCKET', null);
    },
    updateUser(context, data) {
      // if a user is set
      if (!isEmpty(context.getters.user)) {
        data = isNil(data) ? data : data.toLocaleLowerCase();
        if (data === context.getters.username) {
          // if the username is the same - do not update it
          return Promise.resolve();
        } else {
          // if the username is different do reset the stored data
          context.commit('USER', null);
          context.commit('USERNAME', data);
        }
      }

      return axios.get(ServiceUrls.currentUser(), {
        headers: {
          'x-hotelsoftware-hotelid': context.getters.hotelId,
        },
        params: {},
        hotelId: context.getters.hotelId,
      })
        .then((userResponse) => {
          context.commit('USER', userResponse.data.data);
          context.commit('USERNAME', userResponse.data.data.name.toLocaleLowerCase());

          return userResponse;
        })
        .catch((error) => {
          clearToken(context.getters.hotelId);
          context.dispatch('logout');

          throw error;
        });
    },
    login(context, data) {
      context.commit('USER', null);
      context.commit('USERNAME', null);
      // context.commit('TOKEN', null);

      return axios.post(ServiceUrls.login(), {
        subdomain: data.subdomain,
        username: data.username,
        password: data.password,
        whiteLabel: isEmpty(data.whiteLabel) ? null : data.whiteLabel,
      })
        .then((response) => {
          const hotelId = response.headers['x-hotelsoftware-hotelid'];
          const token = response.headers['x-hotelsoftware-token'];

          setToken(hotelId, token);
          // context.commit('TOKEN', token);
          context.dispatch('setHotelId', hotelId);
          context.commit('USERNAME', data.username.toLocaleLowerCase());

          return context.dispatch('updateUser');
        })
        .catch((error) => {
          context.dispatch('logout');
          // context.commit('TOKEN', null);
          throw error;
        });
    },
    unloadHotel(context, data) {
      context.commit('LOADED', false);
      context.commit('HOTEL_SETTINGS', null);
      context.commit('ACCESSIBLE_HOTELS', 1);
      context.commit('HOTELS', []);
      context.commit('HOTEL_ID', null);
      // this.$serviceBus.$emit('logout', { params: null })
    },
    updateHotelSettings(context) {
      const promise = axios.get(ServiceUrls.hotel.get({ $root: { hotel: context.getters.hotelId } }))
        .then((response) => {
          if (response.status === 403 || response.status === 401) {
            context.dispatch('unloadHotel', null);
          }

          return response;
        }).catch((error) => {
          context.dispatch('unloadHotel', null);
          throw error;
        });

      context.dispatch('initialiseStore', { promise });
    },
    setHotelId(context, data) {
      if (isEmpty(data)) {
        context.dispatch('unloadHotel', null);
      } else if (data !== context.getters.hotelId) {
        context.commit('HOTEL_ID', data);

        const promise = axios.get(ServiceUrls.hotel.get({ $root: { hotel: data } }))
          .then((response) => {
            if (response.status === 403 || response.status === 401) {
              context.dispatch('unloadHotel', null);
            }

            return response;
          }).catch((error) => {
            context.dispatch('unloadHotel', null);
            throw error;
          });

        context.dispatch('initialiseStore', { promise });
      }
    },
    initialiseStore(context, data) {
      const promise = data.promise;
      const hotelId = context.getters.hotelId;
      if (!isEmpty(localStorage.getItem(`${hotelId}__hotelSettings`))) {
        try {
          const storeData = JSON.parse(localStorage.getItem(`${hotelId}__hotelSettings`));
          optimizeInputData(storeData);
          context.commit('HOTEL_SETTINGS', storeData);
          context.commit('LOADED', true);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log('Failed to load state from storage');
        }
      }

      promise
        .then((response) => {
          if (!isNil(response) && !isNil(response.data) && !isNil(response.data.data)) {
            optimizeInputData(response.data.data);

            if (!isNil(response) && !isNil(response.data) && !isNil(response.data.data)) {
              context.commit('HOTEL_SETTINGS', Object.freeze(response.data.data));
              context.commit('LOADED', true);
              try {
                localStorage.setItem(`${hotelId}__hotelSettings`, JSON.stringify(response.data.data));
              } catch (e) {
                /* in 99.99% of the cases this will be a "QuotaExceededError: The quota has been exceeded." error, which most likely happens on mobile Safari which limits the localStorage to 5MB */
                window.clearLocalStorage([/^.*__token$/gi]);
              }
            }
          }
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/stores/Hotel.js